import { useEffect, useMemo, useRef, useState } from 'react';
import type { Banner } from '../api/tenant';
import type { TelegramApi } from '../telegram/webapp';
import s from './bannerSlot.module.css';

const ROTATE_MS = 6000;
const PAUSE_MS = 10_000;
const INDEX_KEY = 'bannerIndex';
/** Banners that send the resident to this very mini app make no sense inside it. */
const SELF_LINK = /t\.me\/pass24app_bot/i;

export function visibleBanners(list: readonly Banner[]): Banner[] {
  return list.filter((b) => !(b.bgImageActionLink && SELF_LINK.test(b.bgImageActionLink)));
}

/** The list starting from `start`, wrapping around. */
export function rotate<T>(list: readonly T[], start: number): T[] {
  if (!list.length) return [];
  const i = ((start % list.length) + list.length) % list.length;
  return [...list.slice(i), ...list.slice(0, i)];
}

/** Read once per launch and advanced once: every launch starts from the next partner. */
let launchStart: Promise<number> | null = null;
function startIndex(telegram: TelegramApi): Promise<number> {
  launchStart ??= telegram.storage.get(INDEX_KEY).then((v) => {
    const next = (Number(v) || 0) + 1;
    void telegram.storage.set(INDEX_KEY, String(next));
    return next;
  });
  return launchStart;
}

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

interface Props {
  banners: readonly Banner[];
  telegram: TelegramApi;
  /** Where the slot is: the home screen under the actions or the success screen (decision 2026-10-21). */
  placement: 'home' | 'success';
}

/** Partner banners: one slot, rotating every 6 s, swipeable, never inside forms. */
export function BannerSlot({ banners, telegram, placement }: Props) {
  const [start, setStart] = useState(0);
  const [broken, setBroken] = useState<number[]>([]);
  const [current, setCurrent] = useState(0);
  const track = useRef<HTMLDivElement>(null);
  const pausedUntil = useRef(0);

  useEffect(() => {
    let alive = true;
    void startIndex(telegram).then((n) => alive && setStart(n));
    return () => {
      alive = false;
    };
  }, [telegram]);

  const slides = useMemo(() => rotate(visibleBanners(banners).filter((b) => !broken.includes(b.id)), start), [banners, broken, start]);

  useEffect(() => {
    if (slides.length < 2 || reducedMotion()) return;
    const timer = setInterval(() => {
      const el = track.current;
      if (!el || document.hidden || Date.now() < pausedUntil.current) return;
      const next = (Math.round(el.scrollLeft / (el.clientWidth || 1)) + 1) % slides.length;
      el.scrollTo?.({ left: next * el.clientWidth, behavior: 'smooth' });
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (!slides.length) return null;
  const pause = () => {
    pausedUntil.current = Date.now() + PAUSE_MS;
  };
  return (
    <div className={s.slot} data-placement={placement}>
      <div
        ref={track}
        className={s.track}
        role="region"
        aria-roledescription="карусель"
        aria-label="Предложения партнёров"
        onPointerDown={pause}
        onTouchStart={pause}
        onWheel={pause}
        onScroll={(e) => setCurrent(Math.round(e.currentTarget.scrollLeft / (e.currentTarget.clientWidth || 1)))}
      >
        {slides.map((b, i) => (
          <a
            key={b.id}
            className={s.slide}
            href={b.bgImageActionLink ?? '#'}
            aria-label={`Предложение партнёра ${i + 1} из ${slides.length}`}
            onClick={(e) => {
              e.preventDefault();
              if (b.bgImageActionLink) telegram.openLink(b.bgImageActionLink);
            }}
          >
            <img src={b.bgImageUrl} alt="Предложение партнёра" loading="lazy" onError={() => setBroken((list) => [...list, b.id])} />
          </a>
        ))}
      </div>
      {slides.length > 1 && (
        <div className={s.dots} aria-hidden="true">
          {slides.map((b, i) => (
            <i key={b.id} className={i === current ? `${s.dot} ${s.dotOn}` : s.dot} />
          ))}
        </div>
      )}
    </div>
  );
}
